import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import InputField from "@/components/form/InputField";
import {
  Box,
  Card,
  Flex,
  Text,
  Heading,
  Container,
  Button,
} from "@radix-ui/themes";
import { authService } from "@/services/breeze/AuthService";

const validationSchema = Yup.object({
  password: Yup.string()
    .required("Password is required")
    .min(8, "Password must be at least 8 characters")
    .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
    .matches(/[a-z]/, "Password must contain at least one lowercase letter")
    .matches(/[0-9]/, "Password must contain at least one number")
    .matches(
      /[^A-Za-z0-9]/,
      "Password must contain at least one special character",
    ),
  confirmPassword: Yup.string()
    .required("Password confirmation is required")
    .oneOf([Yup.ref("password")], "Passwords must match"),
});

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isValidToken, setIsValidToken] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [resetToken, setResetToken] = useState("");

  useEffect(() => {
    const validateResetLink = () => {
      try {
        const email = searchParams.get("email");
        const token = searchParams.get("token");

        if (email && token) {
          setUserEmail(email);
          setResetToken(token);
          setIsValidToken(true);
        } else {
          setError(
            "Invalid or expired reset link. Please request a new password reset.",
          );
        }
      } catch (error) {
        console.error("Error validating reset link:", error);
        setError(
          "Invalid or expired reset link. Please request a new password reset.",
        );
      } finally {
        setIsLoading(false);
      }
    };

    validateResetLink();
  }, [searchParams]);

  const handleSubmit = async (values: { password: string }) => {
    setIsSubmitting(true);
    setError("");

    try {
      await authService.confirmPasswordReset(
        userEmail,
        resetToken,
        values.password,
      );

      // Show success message
      setIsSuccess(true);
    } catch (error) {
      console.error("Error resetting password:", error);
      setError(
        "Failed to reset password. Please try again or request a new reset link.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <Container
        size="1"
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Card style={{ width: "100%", maxWidth: "28rem" }}>
          <Box p="6">
            <Flex direction="column" align="center" gap="4">
              <Heading size="6" weight="medium">
                Verifying reset link...
              </Heading>
              <Text color="gray" align="center">
                Please wait while we verify your password reset link.
              </Text>
            </Flex>
          </Box>
        </Card>
      </Container>
    );
  }

  if (!isValidToken) {
    return (
      <Container
        size="1"
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Card style={{ width: "100%", maxWidth: "28rem" }}>
          <Box p="6">
            <Flex direction="column" align="center" gap="4">
              <Heading size="6" weight="medium" color="red">
                Invalid Reset Link
              </Heading>
              <Text color="gray" align="center">
                {error}
              </Text>
              <Text color="gray" align="center" size="2">
                If you believe this is an error, please request a new password
                reset link.
              </Text>
              {searchParams.get("redirect_uri") && (
                <Button
                  onClick={() => {
                    const redirectUri = searchParams.get("redirect_uri");
                    if (redirectUri) window.location.href = redirectUri;
                  }}
                  variant="outline"
                  style={{ width: "100%" }}
                >
                  Continue to Application
                </Button>
              )}
            </Flex>
          </Box>
        </Card>
      </Container>
    );
  }

  // Show success message after password reset
  if (isSuccess) {
    const redirectUri = searchParams.get("redirect_uri");
    return (
      <Container
        size="1"
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Card style={{ width: "100%", maxWidth: "28rem" }}>
          <Box p="6">
            <Flex direction="column" align="center" gap="4">
              <Heading size="6" weight="medium" color="green">
                Password Reset Complete
              </Heading>
              <Text color="gray" align="center">
                Your password has been successfully reset. You can now log in to
                your application using your new password.
              </Text>
              {redirectUri ? (
                <>
                  <Text color="gray" align="center" size="2">
                    Click below to continue to your application.
                  </Text>
                  <Button
                    onClick={() => {
                      window.location.href = redirectUri;
                    }}
                    style={{ width: "100%" }}
                  >
                    Continue to Application
                  </Button>
                </>
              ) : (
                <Text color="gray" align="center" size="2">
                  Please return to your application to log in.
                </Text>
              )}
            </Flex>
          </Box>
        </Card>
      </Container>
    );
  }

  return (
    <Container
      size="1"
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "1rem",
      }}
    >
      <Card style={{ width: "100%", maxWidth: "28rem" }}>
        <Box p="6">
          <Flex direction="column" gap="6">
            <Flex direction="column" align="center" gap="2">
              <Heading size="7" weight="bold" align="center">
                Reset Your Password
              </Heading>
              <Text color="gray" align="center" size="3">
                Enter your new password for{" "}
                <Text weight="medium">{userEmail}</Text>
              </Text>
            </Flex>

            {error && (
              <Box
                p="3"
                style={{
                  backgroundColor: "var(--red-a3)",
                  border: "1px solid var(--red-a6)",
                  borderRadius: "var(--radius-2)",
                }}
              >
                <Text color="red" size="2">
                  {error}
                </Text>
              </Box>
            )}

            <Formik
              initialValues={{
                token: resetToken,
                password: "",
                confirmPassword: "",
              }}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {() => (
                <Form>
                  <Flex direction="column" gap="4">
                    <InputField
                      name="token"
                      label="Token"
                      type="number"
                      required
                      options={{ placeholder: "Enter your token" }}
                      description="Token is an 8 digit code sent to your email"
                    />

                    <InputField
                      name="password"
                      label="New Password"
                      type="password"
                      required
                      options={{
                        placeholder: "Enter your new password",
                        masked: true,
                      }}
                      description="Password must be at least 8 characters with uppercase, lowercase, number, and special character"
                    />

                    <InputField
                      name="confirmPassword"
                      label="Confirm New Password"
                      type="password"
                      required
                      options={{
                        placeholder: "Confirm your new password",
                        masked: true,
                      }}
                    />

                    <Button
                      type="submit"
                      loading={isSubmitting}
                      style={{ width: "100%" }}
                      disabled={isSubmitting}
                    >
                      {isSubmitting
                        ? "Updating Password..."
                        : "Update Password"}
                    </Button>
                  </Flex>
                </Form>
              )}
            </Formik>

            <Box style={{ textAlign: "center" }}>
              <Text size="2" color="gray" align="center">
                Remember your password?{" "}
                {searchParams.get("redirect_uri") ? (
                  <Text
                    style={{ cursor: "pointer" }}
                    color="blue"
                    weight="medium"
                    onClick={() => {
                      const redirectUri = searchParams.get("redirect_uri");
                      if (redirectUri) {
                        window.location.href = redirectUri;
                      }
                    }}
                  >
                    Continue to Application
                  </Text>
                ) : (
                  <Text color="gray" weight="medium">
                    Return to your application to log in
                  </Text>
                )}
              </Text>
            </Box>
          </Flex>
        </Box>
      </Card>
    </Container>
  );
}
